import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle, AlertCircle, ChevronDown, ChevronRight, Download } from "lucide-react";
import { apiConfig } from "../config/api.config";

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
               "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const fCOP = v => {
  if (!v && v !== 0) return "—";
  return new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",
    minimumFractionDigits:0,maximumFractionDigits:0}).format(v);
};
const fPct = v => {
  if (v===null||v===undefined) return "—";
  return parseFloat(v).toFixed(2)+"%";
};

/* Descarga CSV completo */
function descargarCSV(cuentas, anios) {
  const bom = "\uFEFF";
  // Encabezados: CCPET, CRES, NOMBRE, PROYECCIÓN, luego por mes: 2022,2023,2024,2025,PROM,PCT,PAC
  const cabMeses = MESES.flatMap(m=>[
    ...anios.map(a=>`${m} ${a}`),
    `Prom. ${m}`, `% ${m}`, `PAC ${m}`
  ]);
  const cab = ["CCPET","C.RES","NOMBRE","PROYECCIÓN",...cabMeses,"TOTAL PAC"].join(";");

  const rows = cuentas.map(c=>{
    const mData = MESES.flatMap((_,i)=>{
      const m = i+1;
      const md = c.meses?.[m] ?? {};
      return [
        ...anios.map(a=>String(Math.round(md.anios?.[a]||0)).replace(".",",")),
        String(Math.round(md.promedio||0)).replace(".",","),
        fPct(md.pct),
        String(Math.round(md.pac||0)).replace(".",","),
      ];
    });
    return [
      c.ccpet, c.cres??"", `"${c.nombre}"`,
      String(Math.round(c.proyeccion||0)).replace(".",","),
      ...mData,
      String(Math.round(c.total_pac||0)).replace(".",","),
    ].join(";");
  });

  const blob = new Blob([bom+cab+"\n"+rows.join("\n")],{type:"text/csv;charset=utf-8;"});
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `PAC_Ingresos_Consolidado.csv`;
  a.click(); URL.revokeObjectURL(a.href);
}

export default function PACIngresos() {
  const navigate = useNavigate();
  const fRef = useRef();

  const [datos, setDatos]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [msg, setMsg]           = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busqueda, setBusqueda] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        apiConfig.getUrl("/api/hacienda/pac-ingresos/consultar/"),
        {credentials:"include"}
      );
      setDatos(await res.json());
    } catch { setDatos(null); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);

  const handleUp = async e => {
    const files = Array.from(e.target.files??[]);
    if(!files.length) return;
    e.target.value="";
    setSubiendo(true); setMsg(null);
    setProgreso({actual:0,total:files.length,ok:[],errores:[],nombre:""});

    let ok=[], errores=[];
    for(let i=0;i<files.length;i++) {
      const file = files[i];
      setProgreso(p=>({...p,actual:i+1,nombre:file.name}));
      const fd = new FormData(); fd.append("file",file);
      try {
        const res = await fetch(
          apiConfig.getUrl("/api/hacienda/pac-ingresos/cargar/"),
          {method:"POST",body:fd,credentials:"include"}
        );
        const d = await res.json();
        if(d.error) throw new Error(d.error);
        ok.push(`${MESES[(d.mes??1)-1]??''} ${d.vigencia??''}`);
      } catch(err){
        errores.push(`${file.name}: ${err.message}`);
      }
      setProgreso(p=>({...p,ok:[...ok],errores:[...errores]}));
    }
    setSubiendo(false);
    if(ok.length>0){
      setMsg({ok:true,t:`✅ ${ok.length} archivo${ok.length>1?"s":""} cargados${errores.length>0?` · ⚠️ ${errores.length} con error`:""}`});
      load();
    } else {
      setMsg({ok:false,t:`❌ Fallaron todos. ${errores.join(" | ")}`});
    }
    setTimeout(()=>setProgreso(null),4000);
  };

  const anios = datos?.anios ?? [];
  const resumen = datos?.resumen ?? [];
  const cuentas = (datos?.cuentas??[]).filter(c=>{
    const q=busqueda.toLowerCase();
    return !q||c.ccpet.toLowerCase().includes(q)||c.nombre.toLowerCase().includes(q)||(c.cres??"").toLowerCase().includes(q);
  });

  const toggleRow = k => setExpanded(p=>({...p,[k]:!p[k]}));

  const S = {
    page: {minHeight:"100vh",background:"#f3f4f6",padding:"2rem 1.5rem"},
    wrap: {maxWidth:"1500px",margin:"0 auto"},
    card: {background:"#fff",borderRadius:"16px",border:"1px solid #e5e7eb",
           boxShadow:"0 4px 14px rgba(0,0,0,0.05)",marginBottom:"1.5rem",overflow:"hidden"},
    thH:  {padding:"0.55rem 0.75rem",background:"#163b8c",color:"#fff",
           fontWeight:700,fontSize:"0.73rem",textAlign:"left",whiteSpace:"nowrap"},
    thG:  {padding:"0.55rem 0.75rem",background:"#15803d",color:"#fff",
           fontWeight:700,fontSize:"0.73rem",textAlign:"right",whiteSpace:"nowrap"},
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        <button type="button" onClick={()=>navigate("/hacienda-presupuesto")}
          style={{background:"#e5e7eb",color:"#111827",border:"none",borderRadius:"10px",
            padding:"0.7rem 1rem",cursor:"pointer",fontWeight:600,marginBottom:"1.5rem"}}>
          ← Volver a Hacienda
        </button>

        <div style={{marginBottom:"1.5rem"}}>
          <h1 style={{margin:0,color:"#163b8c",fontSize:"2rem",fontWeight:800}}>
            PAC — Plan Anual de Caja · Ingresos
          </h1>
          <p style={{marginTop:"0.5rem",color:"#6b7280"}}>
            Promedio histórico mensual · {anios.length>0?`Años cargados: ${anios.join(", ")}`:"Sin datos aún"}
          </p>
        </div>

        {/* ── Cargue ── */}
        <div style={{...S.card,padding:"1.5rem"}}>
          <h2 style={{margin:"0 0 1rem",color:"#163b8c",fontSize:"1.1rem",fontWeight:700}}>
            Histórico cargado
          </h2>

          {/* Resumen por año */}
          {resumen.length>0&&(
            <div style={{display:"flex",gap:"0.6rem",flexWrap:"wrap",marginBottom:"1rem"}}>
              {resumen.map(r=>(
                <div key={r.vigencia}
                  style={{border:"1px solid #e5e7eb",borderRadius:"12px",
                    padding:"0.65rem 1rem",background:"#f9fafb",minWidth:"120px",textAlign:"center"}}>
                  <div style={{fontWeight:800,fontSize:"1.05rem",color:"#163b8c"}}>{r.vigencia}</div>
                  <div style={{fontSize:"0.72rem",fontWeight:600,marginTop:"0.2rem",
                    color:r.meses_cargados===12?"#16a34a":"#92400e"}}>
                    {r.meses_cargados}/12 meses
                  </div>
                  <div style={{height:"4px",background:"#e5e7eb",borderRadius:"999px",
                    marginTop:"0.4rem",overflow:"hidden"}}>
                    <div style={{height:"4px",borderRadius:"999px",
                      background:r.meses_cargados===12?"#16a34a":"#f59e0b",
                      width:`${(r.meses_cargados/12)*100}%`}}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",alignItems:"center",gap:"0.75rem",
            flexWrap:"wrap",marginBottom:"0.75rem"}}>
            <input ref={fRef} type="file" accept=".csv,.xlsx,.xls"
              multiple style={{display:"none"}} onChange={handleUp}/>
            <button type="button" onClick={()=>fRef.current.click()} disabled={subiendo}
              style={{display:"flex",alignItems:"center",gap:"0.5rem",
                background:subiendo?"#93c5fd":"#163b8c",color:"#fff",border:"none",
                borderRadius:"10px",padding:"0.75rem 1.3rem",fontWeight:700,
                cursor:subiendo?"not-allowed":"pointer",
                boxShadow:"0 4px 12px rgba(22,59,140,0.2)"}}>
              <Upload size={18}/>
              {subiendo?(progreso?`Cargando ${progreso.actual}/${progreso.total}...`:"Cargando..."):"Cargar archivos (múltiple)"}
            </button>
            <span style={{fontSize:"0.82rem",color:"#9ca3af"}}>
              Mismo formato CSV de ejecución. El año y mes se detectan automáticamente. Máx 5 años × 12 meses.
            </span>
          </div>

          {/* Barra de progreso */}
          {progreso&&(
            <div style={{padding:"0.85rem 1rem",borderRadius:"10px",
              background:"#eff6ff",border:"1px solid #dbeafe",marginBottom:"0.75rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:"0.82rem",fontWeight:600,color:"#163b8c",marginBottom:"0.5rem"}}>
                <span style={{maxWidth:"80%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {progreso.nombre||"Iniciando..."}
                </span>
                <span>{progreso.actual}/{progreso.total}</span>
              </div>
              <div style={{height:"8px",background:"#dbeafe",borderRadius:"999px",overflow:"hidden"}}>
                <div style={{height:"8px",background:"#163b8c",borderRadius:"999px",
                  width:`${progreso.total>0?(progreso.actual/progreso.total)*100:0}%`,
                  transition:"width 0.3s"}}/>
              </div>
              {progreso.ok.length>0&&(
                <div style={{fontSize:"0.75rem",color:"#15803d",marginTop:"0.4rem"}}>
                  ✅ {progreso.ok.length}: {progreso.ok.slice(-3).join(" · ")}
                  {progreso.ok.length>3?"...":""}
                </div>
              )}
              {progreso.errores.length>0&&(
                <div style={{fontSize:"0.75rem",color:"#dc2626",marginTop:"0.2rem"}}>
                  ❌ {progreso.errores.join(" · ")}
                </div>
              )}
            </div>
          )}

          {msg&&(
            <div style={{padding:"0.85rem 1rem",borderRadius:"10px",
              background:msg.ok?"#f0fdf4":"#fef2f2",
              border:`1px solid ${msg.ok?"#bbf7d0":"#fca5a5"}`,
              color:msg.ok?"#15803d":"#991b1b",
              display:"flex",alignItems:"center",gap:"0.5rem",fontSize:"0.9rem"}}>
              {msg.ok?<CheckCircle size={16}/>:<AlertCircle size={16}/>}{msg.t}
            </div>
          )}
        </div>

        {/* ── Tabla PAC ── */}
        {!loading&&cuentas.length>0&&(
          <div style={S.card}>
            <div style={{padding:"1rem 1.5rem",borderBottom:"1px solid #e5e7eb",
              display:"flex",justifyContent:"space-between",alignItems:"center",
              flexWrap:"wrap",gap:"0.75rem"}}>
              <input type="text" placeholder="Buscar CCPET, nombre, C.RES..."
                value={busqueda} onChange={e=>setBusqueda(e.target.value)}
                style={{padding:"0.55rem 1rem",borderRadius:"8px",border:"1px solid #d1d5db",
                  fontSize:"0.88rem",minWidth:"260px",outline:"none"}}/>
              <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                <span style={{fontSize:"0.82rem",color:"#6b7280"}}>{cuentas.length} cuentas</span>
                <button type="button"
                  onClick={()=>descargarCSV(datos.cuentas, anios)}
                  style={{display:"flex",alignItems:"center",gap:"0.5rem",
                    background:"#163b8c",color:"#fff",border:"none",borderRadius:"8px",
                    padding:"0.55rem 1rem",fontWeight:700,fontSize:"0.82rem",cursor:"pointer"}}>
                  <Download size={14}/> Descargar PAC completo
                </button>
              </div>
            </div>

            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={{...S.thH,width:"28px"}}></th>
                  <th style={S.thH}>CCPET</th>
                  <th style={S.thH}>C.RES</th>
                  <th style={{...S.thH,minWidth:"200px"}}>NOMBRE</th>
                  <th style={{...S.thH,textAlign:"right"}}>Proyección</th>
                  <th style={{...S.thG}}>Total PAC</th>
                </tr></thead>
                <tbody>
                  {cuentas.map((c,i)=>{
                    const isOpen = !!expanded[c.ccpet+c.cres];
                    const bg = i%2===0?"#f9fafb":"#fff";
                    const pctCumpl = c.proyeccion>0 ? c.total_pac/c.proyeccion : 0;
                    return [
                      /* Fila principal */
                      <tr key={c.ccpet+c.cres}
                        onClick={()=>toggleRow(c.ccpet+c.cres)}
                        style={{cursor:"pointer",background:isOpen?"#eff6ff":bg,
                          borderBottom:"1px solid #e5e7eb"}}>
                        <td style={{padding:"0.5rem 0.5rem 0.5rem 0.75rem",color:"#163b8c",verticalAlign:"middle"}}>
                          {isOpen?<ChevronDown size={15}/>:<ChevronRight size={15}/>}
                        </td>
                        <td style={{padding:"0.5rem 0.75rem",verticalAlign:"middle"}}>
                          <code style={{fontSize:"0.7rem",color:"#374151"}}>{c.ccpet}</code>
                        </td>
                        <td style={{padding:"0.5rem 0.75rem",fontSize:"0.72rem",color:"#6b7280",verticalAlign:"middle"}}>
                          {c.cres??"—"}
                        </td>
                        <td style={{padding:"0.5rem 0.75rem",fontSize:"0.8rem",maxWidth:"240px",verticalAlign:"middle"}}>
                          {c.nombre}
                        </td>
                        <td style={{padding:"0.5rem 0.75rem",textAlign:"right",fontFamily:"monospace",
                          fontSize:"0.78rem",fontWeight:600,verticalAlign:"middle"}}>
                          {fCOP(c.proyeccion)}
                        </td>
                        <td style={{padding:"0.5rem 0.75rem",textAlign:"right",fontFamily:"monospace",
                          fontSize:"0.78rem",fontWeight:700,color:"#065f46",
                          background:i%2===0?"#dcfce7":"#bbf7d0",verticalAlign:"middle"}}>
                          {fCOP(c.total_pac)}
                        </td>
                      </tr>,

                      /* Fila expandida — detalle por mes igual al Excel */
                      isOpen&&(
                        <tr key={c.ccpet+c.cres+"_exp"}>
                          <td colSpan={6} style={{padding:"1rem 1.5rem",
                            background:"#f8faff",borderBottom:"2px solid #dbeafe"}}>

                            <div style={{fontSize:"0.82rem",fontWeight:700,color:"#163b8c",marginBottom:"0.75rem"}}>
                              {c.nombre} — Distribución mensual histórica
                            </div>

                            {/* Grid de 12 meses — 2 filas × 6 */}
                            {[0,1].map(fila=>(
                              <div key={fila} style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",
                                gap:"0.5rem",marginBottom:fila===0?"0.5rem":0}}>
                                {Array.from({length:6},(_,j)=>{
                                  const mesIdx = fila*6+j;
                                  const mes = mesIdx+1;
                                  const md = c.meses?.[mes] ?? {};
                                  const prom = md.promedio??0;
                                  const pct  = md.pct??0;
                                  const pac  = md.pac??0;
                                  return (
                                    <div key={mes} style={{background:"#fff",borderRadius:"12px",
                                      border:"1px solid #e5e7eb",padding:"0.75rem",
                                      boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>

                                      {/* Nombre del mes */}
                                      <div style={{fontWeight:700,color:"#163b8c",
                                        fontSize:"0.8rem",marginBottom:"0.5rem",
                                        borderBottom:"1px solid #f3f4f6",paddingBottom:"0.35rem"}}>
                                        {MESES[mesIdx]}
                                      </div>

                                      {/* Años históricos */}
                                      {anios.map(a=>(
                                        <div key={a} style={{display:"flex",justifyContent:"space-between",
                                          fontSize:"0.69rem",color:"#6b7280",marginBottom:"0.15rem"}}>
                                          <span>{a}</span>
                                          <span style={{fontFamily:"monospace",fontWeight:500}}>
                                            {fCOP(Math.abs(md.anios?.[a]??0))}
                                          </span>
                                        </div>
                                      ))}

                                      {/* Separador */}
                                      <div style={{borderTop:"1px solid #e5e7eb",margin:"0.4rem 0"}}/>

                                      {/* Promedio */}
                                      <div style={{display:"flex",justifyContent:"space-between",
                                        fontSize:"0.72rem",marginBottom:"0.2rem"}}>
                                        <span style={{color:"#374151",fontWeight:600}}>Prom. recaudo</span>
                                        <span style={{fontFamily:"monospace",fontWeight:700,color:"#1e40af"}}>
                                          {fCOP(prom)}
                                        </span>
                                      </div>

                                      {/* % */}
                                      <div style={{display:"flex",justifyContent:"space-between",
                                        fontSize:"0.72rem",marginBottom:"0.2rem"}}>
                                        <span style={{color:"#374151",fontWeight:600}}>Prom. %</span>
                                        <span style={{fontFamily:"monospace",fontWeight:700,color:"#7c3aed"}}>
                                          {fPct(pct)}
                                        </span>
                                      </div>

                                      {/* Valor PAC */}
                                      <div style={{display:"flex",justifyContent:"space-between",
                                        fontSize:"0.76rem",marginTop:"0.35rem",
                                        padding:"0.3rem 0.4rem",borderRadius:"6px",
                                        background:"#f0fdf4"}}>
                                        <span style={{color:"#065f46",fontWeight:700}}>Valor PAC</span>
                                        <span style={{fontFamily:"monospace",fontWeight:800,color:"#065f46"}}>
                                          {fCOP(pac)}
                                        </span>
                                      </div>

                                      {/* Mini barra del % */}
                                      <div style={{height:"3px",background:"#e5e7eb",
                                        borderRadius:"999px",marginTop:"0.4rem",overflow:"hidden"}}>
                                        <div style={{height:"3px",background:"#163b8c",
                                          borderRadius:"999px",
                                          width:`${Math.min(pct*12,100)}%`}}/>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}

                            {/* Totales del año */}
                            <div style={{marginTop:"0.75rem",padding:"0.65rem 1rem",
                              borderRadius:"10px",background:"#163b8c",color:"#fff",
                              display:"flex",justifyContent:"space-between",
                              alignItems:"center",flexWrap:"wrap",gap:"1rem"}}>
                              <span style={{fontWeight:700,fontSize:"0.85rem"}}>
                                Total promedio anual: {fCOP(c.total_promedio_anual)}
                              </span>
                              <span style={{fontWeight:700,fontSize:"0.85rem"}}>
                                Proyección: {fCOP(c.proyeccion)}
                              </span>
                              <span style={{fontWeight:800,fontSize:"0.9rem",
                                color:"#a7f3d0"}}>
                                Total PAC: {fCOP(c.total_pac)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {loading&&(
          <div style={{textAlign:"center",color:"#6b7280",padding:"3rem"}}>
            Cargando PAC...
          </div>
        )}
        {!loading&&(datos?.cuentas??[]).length===0&&(
          <div style={{background:"#fff",borderRadius:"16px",border:"1px solid #e5e7eb",
            padding:"3rem",textAlign:"center",color:"#6b7280"}}>
            Carga los archivos CSV mes a mes para calcular el PAC.<br/>
            <span style={{fontSize:"0.85rem",display:"block",marginTop:"0.5rem"}}>
              Se detecta automáticamente el año y mes de cada archivo.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}